/**
 * Screen 27 — Invite & Earn.
 *
 * Surfaces the user's referral link + totals + the list of friends
 * they've invited. The earn loop is: share link → friend signs up via
 * link → friend plays their first live trivia → you get +1 50/50.
 *
 * 50/50s are also purchasable from /fiftyfifty (paid bundles). This
 * page links to that shop as a "top up now" option for users who don't
 * want to wait for their next referral conversion — referrals are still
 * the cheapest path (free), so the page leads with the share link and
 * keeps the buy CTA below the fold.
 */

import { api, ApiError } from '../api.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';

export async function renderInvite(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Invite & Earn</div>
    </div>
    <div class="pad" id="invite-body">
      <div class="card muted">Loading…</div>
    </div>
  `;

  let summary;
  try {
    summary = await api('/referrals/me');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    document.getElementById('invite-body').innerHTML =
      `<div class="card" style="color:var(--pink-t)">${escape(err.message || 'Could not load referrals.')}</div>`;
    return;
  }

  const body = document.getElementById('invite-body');
  body.innerHTML = `
    <div class="hero violet center" style="padding:24px 16px;">
      <div style="font-family:var(--font-display);font-size:12px;letter-spacing:5px;font-weight:700;opacity:0.92;">
        50/50 POWER-UPS
      </div>
      <div style="font-family:var(--font-display);font-size:46px;color:#fff;font-weight:800;margin:8px 0;letter-spacing:-0.5px;">
        ${summary.fiftyfifty_balance}
      </div>
      <div style="font-size:12.5px;opacity:0.9;">
        in your inventory · earn more by inviting friends
      </div>
    </div>

    <div class="card" style="padding:14px;">
      <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:6px;">
        Your referral link
      </div>
      <div style="font-family:var(--font-display);font-size:13px;color:#fff;word-break:break-all;line-height:1.45;">
        ${escape(summary.share_url || '')}
      </div>
      <div class="row" style="margin-top:12px;gap:8px;flex-wrap:wrap;">
        <button type="button" id="invite-copy" class="btn"
                style="flex:1;min-width:140px;background:rgba(233,75,162,0.18);border:1px solid rgba(233,75,162,0.55);color:#fff;">
          Copy link
        </button>
        <button type="button" id="invite-share" class="btn"
                style="flex:1;min-width:140px;">
          Share…
        </button>
      </div>
      <div class="muted" style="font-size:11px;margin-top:8px;">
        Code: <b style="color:#fff;font-family:var(--font-display);letter-spacing:0.12em;">${escape(summary.code || '')}</b>
      </div>
    </div>

    <div class="card" style="padding:14px;">
      <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px;">
        How it works
      </div>
      <ol style="margin:0;padding-left:20px;line-height:1.55;font-size:13px;color:#fff;">
        <li>Share your link with a friend.</li>
        <li>They sign up via your link.</li>
        <li>When they play their first <b>live</b> trivia, you earn <b>1 × 50/50</b>.</li>
      </ol>
      <div class="muted" style="font-size:11.5px;margin-top:10px;line-height:1.45;">
        50/50s are also awarded automatically to players who finish in an admin-configured rank range on live trivias. Max 2 uses per live game.
      </div>
    </div>

    <a href="/shop" data-link class="card" style="display:block;text-decoration:none;padding:14px;border:1px solid rgba(127,180,255,0.42);background:rgba(127,180,255,0.06);">
      <div class="row" style="gap:10px;align-items:center;">
        <div style="width:38px;height:38px;flex-shrink:0;background:rgba(127,180,255,0.18);border:1px solid rgba(127,180,255,0.42);border-radius:10px;display:flex;align-items:center;justify-content:center;color:#7FB4FF;font-family:var(--font-display);font-size:14px;font-weight:800;">
          ₦
        </div>
        <div class="grow" style="min-width:0;">
          <div style="font-size:14px;color:#fff;font-weight:700;">Need them now? Buy a bundle</div>
          <div class="muted" style="font-size:11.5px;margin-top:2px;">From ₦250 · pay with wallet or bonus credit</div>
        </div>
        <div style="color:#7FB4FF;font-weight:800;font-family:var(--font-display);">›</div>
      </div>
    </a>

    <div class="row" style="gap:8px;">
      <div class="card grow center" style="padding:14px;">
        <div style="font-family:var(--font-display);font-size:26px;color:#fff;font-weight:700;">${summary.total_referrals}</div>
        <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-top:2px;">Invited</div>
      </div>
      <div class="card grow center" style="padding:14px;background:rgba(233,75,162,0.08);border:1px solid rgba(233,75,162,0.35);">
        <div style="font-family:var(--font-display);font-size:26px;color:#E94BA2;font-weight:700;">${summary.rewarded_referrals}</div>
        <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-top:2px;">Rewarded</div>
      </div>
      <div class="card grow center" style="padding:14px;">
        <div style="font-family:var(--font-display);font-size:26px;color:#FFE9A6;font-weight:700;">${summary.pending_referrals}</div>
        <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-top:2px;">Pending</div>
      </div>
    </div>

    <div class="card" style="padding:12px;">
      <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px;">
        Friends you've referred
      </div>
      ${renderFriendsList(summary.friends || [])}
    </div>
  `;

  wireButtons(summary);
}

function renderFriendsList(friends) {
  if (!friends.length) {
    return `
      <div class="muted" style="font-size:12.5px;padding:6px 2px;">
        No referrals yet. Share your link to start earning 50/50s.
      </div>
    `;
  }
  return friends.map((f) => {
    const isRewarded = f.status === 'rewarded';
    const chipBg = isRewarded ? 'rgba(233,75,162,0.18)' : '#1A1432';
    const chipBorder = isRewarded ? 'rgba(233,75,162,0.55)' : '#2A2244';
    const chipColor = isRewarded ? '#E94BA2' : 'var(--mute)';
    const chipText = isRewarded ? '🎯 Played · +1' : '⏳ Pending';
    return `
      <div class="row" style="padding:8px 0;border-top:1px solid #2A2244;">
        <span style="font-size:13px;color:#fff;flex:1;">
          ${escape(f.referred_name || 'A friend')}
        </span>
        <span style="display:inline-flex;align-items:center;padding:3px 8px;border-radius:999px;font-size:10.5px;font-weight:700;background:${chipBg};border:1px solid ${chipBorder};color:${chipColor};font-family:var(--font-display);letter-spacing:0.05em;">
          ${chipText}
        </span>
      </div>
    `;
  }).join('');
}

function wireButtons(summary) {
  const shareUrl = summary.share_url || '';
  const copyBtn = document.getElementById('invite-copy');
  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Invite link copied.');
      } catch {
        // Older WebView fallback — surface a selectable copy so the
        // user can pick it manually.
        toast.error('Copy failed — long-press the link to copy it.');
      }
    });
  }
  const shareBtn = document.getElementById('invite-share');
  if (shareBtn) {
    // Web Share API on supporting browsers (mobile Safari, Chrome
    // Android); fallback to copy on desktop.
    shareBtn.addEventListener('click', async () => {
      const text = `Play live trivia on Triviana — use my link to sign up: ${shareUrl}`;
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Join me on Triviana',
            text,
            url: shareUrl,
          });
        } catch (err) {
          // AbortError = user cancelled the picker. Don't toast.
          if (err && err.name !== 'AbortError') {
            toast.error('Share failed — try Copy link instead.');
          }
        }
      } else {
        try {
          await navigator.clipboard.writeText(text);
          toast.success('Invite copied to clipboard.');
        } catch {
          toast.error('Long-press the link above to copy it.');
        }
      }
    });
  }
}
